import React, { useEffect, useRef, useState } from 'react';
import { X, ImagePlus } from 'lucide-react';
import { SuggestedTrend } from '@crewcast/shared';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import { usePointsEnabled } from '../hooks/usePointsEnabled';
import { useBackdropDismiss } from '../hooks/useBackdropDismiss';

interface MomentCreatorModalProps {
  trend: SuggestedTrend;
  onClose: () => void;
  onLaunched: (trendId: string, challengeId: string) => void;
}

const VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const VALID_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-m4v'];
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

export default function MomentCreatorModal({
  trend,
  onClose,
  onLaunched,
}: MomentCreatorModalProps) {
  const { token } = useAuth();
  const pointsEnabled = usePointsEnabled();
  const [title, setTitle] = useState(trend.title);
  const [description, setDescription] = useState(trend.description);
  const [rules, setRules] = useState('');
  const [points, setPoints] = useState('');

  // Image state:
  // - imageUrl: the committed URL (either trend's original or newly uploaded)
  // - imageFile: a new file the admin picked to replace/add
  // - imagePreview: local object URL for previewing imageFile before upload
  const [imageUrl, setImageUrl] = useState<string>(trend.image || '');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  const [collaboratorsEnabled, setCollaboratorsEnabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const backdrop = useBackdropDismiss(onClose);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (![...VALID_IMAGE_TYPES, ...VALID_VIDEO_TYPES].includes(file.type)) {
      setError('Please upload an image (JPG, PNG, GIF, WEBP) or video (MP4, MOV).');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError(`File must be under 100MB (current: ${Math.round(file.size / (1024 * 1024))}MB).`);
      return;
    }

    setError('');
    setImageFile(file);
    setImageUrl(''); // clear committed URL — new file takes precedence

    if (file.type.startsWith('video/')) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  }

  function handleRemoveImage() {
    setImageFile(null);
    setImagePreview('');
    setImageUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // True when there is something to preview (file picked or original URL kept)
  const previewSrc = imagePreview || imageUrl;
  const isVideo =
    imageFile?.type.startsWith('video/') ||
    (!imageFile && imageUrl.match(/\.(mp4|mov|m4v)$/i));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    const pointsNum = pointsEnabled ? parseInt(points, 10) : 0;
    if (!title.trim()) { setError('Title is required.'); return; }
    if (!rules.trim()) { setError('Rules are required.'); return; }
    if (pointsEnabled && (!points || isNaN(pointsNum) || pointsNum <= 0)) {
      setError('Points must be a positive number.');
      return;
    }

    setSubmitting(true);
    try {
      if (!token) throw new Error('Not authenticated');

      let finalImageUrl: string | undefined = imageUrl || undefined;

      // Upload new file if the admin picked one
      if (imageFile) {
        setUploadingImage(true);
        const presignedRes = await fetch(`${authConfig.apiUrl}/api/upload/presigned-url`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            fileName: imageFile.name,
            fileType: imageFile.type,
            purpose: 'challenge',
          }),
        });

        if (!presignedRes.ok) throw new Error('Failed to get upload URL');

        const { uploadUrl, publicUrl } = await presignedRes.json();

        const uploadRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: imageFile,
          headers: { 'Content-Type': imageFile.type },
        });

        if (!uploadRes.ok) throw new Error('Failed to upload image');

        finalImageUrl = publicUrl;
        setUploadingImage(false);
      }

      // Create the challenge as active
      const createRes = await fetch(`${authConfig.apiUrl}/api/admin/challenges`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          rules: rules.trim(),
          points: pointsNum,
          status: 'active',
          collaboratorsEnabled,
          image: finalImageUrl,
        }),
      });

      if (!createRes.ok) {
        const data = await createRes.json();
        throw new Error(data.message || 'Failed to create Moment');
      }

      const { challenge } = await createRes.json();

      // Mark trend as converted
      const convertRes = await fetch(
        `${authConfig.apiUrl}/api/admin/suggested-trends/${trend.trendId}/convert`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ challengeId: challenge.challengeId }),
        }
      );

      if (!convertRes.ok) {
        console.error('Failed to mark trend as converted');
      }

      onLaunched(trend.trendId, challenge.challengeId);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
      setUploadingImage(false);
    }
  }

  function buttonLabel() {
    if (uploadingImage) return 'Uploading media…';
    if (submitting) return 'Launching…';
    return 'Launch Moment';
  }

  return (
    <div
      className="crewcast-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="moment-creator-title"
      {...backdrop}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
      tabIndex={-1}
    >
      <div className="crewcast-modal-card" style={{ maxWidth: '560px' }}>
        <div className="crewcast-modal-header centered">
          <h2 id="moment-creator-title" className="crewcast-modal-title">
            Launch Moment
          </h2>
          <button
            type="button"
            className="crewcast-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="crewcast-modal-body">
            <div className="moment-creator-hint crewcast-modal-hint">
              Title and description are pre-filled from the trend. Add rules and
              points to launch.
            </div>

            <div className="moment-creator-field">
              <label className="moment-creator-label" htmlFor="mc-title">
                Title <span className="moment-creator-required">*</span>
              </label>
              <input
                id="mc-title"
                type="text"
                className="moment-creator-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                required
              />
            </div>

            <div className="moment-creator-field">
              <label className="moment-creator-label" htmlFor="mc-description">
                Description
              </label>
              <textarea
                id="mc-description"
                className="crewcast-modal-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={1000}
              />
            </div>

            <div className="moment-creator-field">
              <label className="moment-creator-label" htmlFor="mc-rules">
                Rules <span className="moment-creator-required">*</span>
              </label>
              <textarea
                id="mc-rules"
                className="crewcast-modal-textarea"
                value={rules}
                onChange={(e) => setRules(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder="e.g. Submit a 30–60 second video. Must be filmed in the last 7 days."
                required
              />
            </div>

            {pointsEnabled && (
            <div className="moment-creator-field">
              <label className="moment-creator-label" htmlFor="mc-points">
                Points <span className="moment-creator-required">*</span>
              </label>
              <input
                id="mc-points"
                type="text"
                inputMode="numeric"
                className="moment-creator-input"
                value={points}
                onChange={(e) => setPoints(e.target.value.replace(/\D/g, ''))}
                placeholder="e.g. 100"
                required
              />
            </div>
            )}

            <div className="moment-creator-field">
              <label className="moment-creator-checkbox-label">
                <input
                  type="checkbox"
                  checked={collaboratorsEnabled}
                  onChange={(e) => setCollaboratorsEnabled(e.target.checked)}
                />
                <span>Enable Collaborators (Team Moment)</span>
              </label>
              {collaboratorsEnabled && (
                <p className="moment-creator-checkbox-hint">
                  Employees will be able to tag teammates on their submissions. All participants receive full points when approved.
                </p>
              )}
            </div>

            {/* Image / Video */}
            <div className="moment-creator-field">
              <label className="moment-creator-label">
                Image / Video
                <span className="moment-creator-optional"> (optional)</span>
              </label>

              {previewSrc ? (
                <div className="moment-creator-image-preview">
                  {isVideo ? (
                    <video
                      src={previewSrc}
                      className="moment-creator-preview-media"
                      controls
                      playsInline
                    />
                  ) : (
                    <img
                      src={previewSrc}
                      alt="Moment preview"
                      className="moment-creator-preview-media"
                    />
                  )}
                  <div className="moment-creator-image-actions">
                    <button
                      type="button"
                      className="moment-creator-image-btn"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Replace
                    </button>
                    <button
                      type="button"
                      className="moment-creator-image-btn danger"
                      onClick={handleRemoveImage}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="moment-creator-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <ImagePlus size={16} />
                  Upload image or video
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/mp4,video/quicktime,video/x-m4v"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>

            {error && <div className="crewcast-modal-error">{error}</div>}
          </div>

          <div className="crewcast-modal-actions">
            <button
              type="button"
              className="crewcast-modal-btn secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="crewcast-modal-btn primary"
              disabled={submitting || uploadingImage}
            >
              {buttonLabel()}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
